import { AI_CREDITS_EXHAUSTED, authDenialReasons, type AuthDenialReason } from '@flintmint/core-shared/constants';

import { reportAuthDenial } from '@/providers/services-provider';

// The `fetch` behind every tiptapAgent request (chat transport and prompt mode). A tab that was
// already open when its access changed only learns about it from a refused request, so the denial
// is reported here, where the status is still known - the chat transport reduces a failed response
// to its body text. The same holds for the AI credits refusal, which callers end quietly rather than
// as a failure. The body is read only for a 402 or 403; every other response passes through untouched.
export async function fetchAgent(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 402 && response.status !== 403) return response;

  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as { error?: unknown } | null;

  if (response.status === 402) {
    if (body?.error === AI_CREDITS_EXHAUSTED) throw new AiCreditsExhaustedError();
    return response;
  }

  const authDenial = authDenialReasons.find((reason) => reason === body?.error);
  if (!authDenial) return response;

  reportAuthDenial(authDenial);
  throw new AuthDeniedError(authDenial);
}

// Thrown instead of the generic request failure so the run terminals can end the refused run
// quietly: the session-level consequence (agreements screen, or sign-out) is already underway via
// `reportAuthDenial`, so there is no error to show or report for the run itself.
export class AuthDeniedError extends Error {
  constructor(reason: AuthDenialReason) {
    super(reason);
    this.name = 'AuthDeniedError';
  }
}

// Thrown instead of the generic request failure when the account has no AI credits left. It is an
// expected billing state, not a fault, so callers end the run quietly and invite the user to upgrade.
export class AiCreditsExhaustedError extends Error {
  constructor() {
    super(AI_CREDITS_EXHAUSTED);
    this.name = 'AiCreditsExhaustedError';
  }
}
